import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { environment } from 'src/environments/environment';
import { PrivateAppDialogComponent } from '../components/private-app-dialog/private-app-dialog.component';

/**
 * The pages that need no admin API and say nothing that depends on it: the site. Everything else
 * is the console. A first segment, or the empty string for the home page.
 *
 * /pricing is here since 2026-10-08: the newsletter of the switch points to it before the
 * console moves. Its buttons lead to the sign-up and to the console, where this guard still acts.
 */
const SITE = new Set([
  '',
  'pricing',
  'features',
  'starters',
  'plugins',
  'sophia',
  'vibe-coding',
  'blog',
  'docs',
  'terms',
  'privacy',
  'cookies',
  'mongo-atlas-data-api-replacement',
  'mongo-data-api-replacement',
  'why-restheart-cloud',
  'not-found',
]);

/** Whether the url is a page of the site, as opposed to one of the console. */
export function isSitePage(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return SITE.has(path.split('/')[1] ?? '');
}

/**
 * While environment.PRIVATE_APP_URL is set, this build serves the site only: a page of the
 * console is not shown, and a dialog offers the same path at that address, where the console
 * runs.
 *
 * A guard and not a redirect at the edge, because the links of the site to the console are
 * router links: the edge sees page loads, not navigations. It covers both, as a deep link to a
 * page of the console loads the app, which then navigates to it.
 */
export const privateAppGuard: CanActivateFn = (_route, state) => {
  const consoleUrl = environment.PRIVATE_APP_URL;

  if (!consoleUrl || !isPlatformBrowser(inject(PLATFORM_ID)) || isSitePage(state.url)) {
    return true;
  }

  const router = inject(Router);

  inject(HlmDialogService).open(PrivateAppDialogComponent, {
    context: { to: consoleUrl.replace(/\/$/, '') + state.url },
  });

  // From a link of the site the reader stays on the page they were on, under the dialog. A deep
  // link has no such page: the home page takes its place.
  return router.navigated ? false : router.parseUrl('/');
};
